import React, { useContext, useEffect, useRef } from "react"
import { TouchableOpacity, View } from "react-native"
import { connect } from "react-redux"
import { useNavigation } from "@react-navigation/native"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import TextM from "@common/components/text/TextM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { apiRequest } from "@data/redux/actions/api"
import { schoolAssessmentsService } from "@data/services/schoolAssessmentsService"
import { ActivityIndicator } from "react-native-paper"
import moment from "moment"
import "moment/locale/fr"

moment.locale("fr")

const urgencyColors = {
    low: theme.colors.success,
    medium: theme.colors.warning,
    high: theme.colors.warningDark || "#FF9800",
    critical: theme.colors.error,
}

const NextAssessmentWidget = (props) => {
    const {
        user,
        loading,
        // Optional props for reusability
        studentId: propStudentId,
        student,
        assessment: propAssessment,
        onPress: customOnPress,
        highlighted = false,
        style,
    } = props
    const navigation = useNavigation()
    const lastFetchedStudentIdRef = useRef(null)
    const dimensions = useContext(DimensionsContext)

    // Determine studentId: prop > current user
    const studentId = propStudentId || user?.id

    // Get assessments from Redux (keyed by studentId)
    const reduxAssessments = props.assessmentsByStudentId?.[studentId]

    // Use prop assessment if provided, otherwise use Redux
    const assessments = propAssessment ? [propAssessment] : (reduxAssessments || [])

    // Check if we have data for this specific student (undefined means never fetched, [] means fetched but empty)
    const hasDataForStudent = reduxAssessments !== undefined

    // Fetch assessments if not already in Redux for this student
    useEffect(() => {
        if (studentId && lastFetchedStudentIdRef.current !== studentId) {
            lastFetchedStudentIdRef.current = studentId
            props.apiRequest(
                schoolAssessmentsService.getSchoolAssessments,
                {},
                {},
                { student_id: studentId, upcoming: true }
            )
        }
    }, [studentId])

    // Get the next upcoming assessment (first one, they're sorted by date)
    const nextAssessment = assessments?.[0]

    const handlePress = () => {
        if (customOnPress) {
            customOnPress()
        } else {
            // Pass studentId if viewing another student's data (tutor context)
            navigation.navigate("school-assessments", propStudentId ? { studentId: propStudentId, student } : {})
        }
    }

    // Loading state - show skeleton while fetching
    const isLoading = loading && !hasDataForStudent

    if (isLoading) {
        return (
            <View
                style={{
                    backgroundColor: theme.colors.lighter,
                    borderRadius: 10,
                    borderWidth: 1,
                    borderColor: theme.colors.lightMore,
                    width: dimensions?.width - 20,
                    alignSelf: "center",
                    marginTop: 10,
                    paddingVertical: 12,
                    paddingHorizontal: 15,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 52,
                    ...style,
                }}
            >
                <TextM
                    fontSize={12}
                    color={theme.colors.grey}
                    style={{ marginLeft: 10 }}
                >
                    Chargement...
                </TextM>
            </View>
        )
    }

    // Empty state - no upcoming assessments
    if (!nextAssessment) {
        const emptyLabel = "Ajouter un contrôle"

        return (
            <TouchableOpacity
                onPress={handlePress}
                activeOpacity={0.8}
                style={{
                    backgroundColor: highlighted
                        ? theme.colors.linkLighter
                        : theme.colors.primaryLighter,
                    borderRadius: 10,
                    borderWidth: highlighted ? 2 : 1,
                    borderColor: highlighted
                        ? theme.colors.link
                        : theme.colors.primary,
                    borderStyle: highlighted ? "solid" : "dashed",
                    width: dimensions?.width - 20,
                    alignSelf: "center",
                    marginTop: 10,
                    paddingVertical: 12,
                    paddingHorizontal: 15,
                    flexDirection: "row",
                    alignItems: "center",
                    ...style,
                }}
            >
                <FontAwesome5
                    name="calendar-plus"
                    size={18}
                    color={theme.colors.primary}
                />
                <TextM
                    fontSize={14}
                    fontWeight="SemiBold"
                    color={theme.colors.primary}
                    style={{ marginLeft: 10, flex: 1 }}
                >
                    {emptyLabel}
                </TextM>
                <FontAwesome5
                    name="chevron-right"
                    size={12}
                    color={theme.colors.primary}
                    style={{ marginLeft: "auto" }}
                />
            </TouchableOpacity>
        )
    }

    const { title, date, school_subject, days_until, urgency, preparation_progress } = nextAssessment

    const displayDate = moment(date).format("ddd D MMM")
    const daysLabel = `J-${days_until}`
    const urgencyColor = urgencyColors[urgency] || theme.colors.grey
    const subjectName = school_subject?.name || "Contrôle"
    const displayTitle = title || subjectName
    const progress = preparation_progress || 0

    return (
        <TouchableOpacity
            onPress={handlePress}
            activeOpacity={0.8}
            style={{
                backgroundColor: highlighted
                    ? theme.colors.linkLighter
                    : theme.colors.white,
                borderRadius: 10,
                borderWidth: highlighted ? 2 : 1,
                borderColor: highlighted
                    ? theme.colors.link
                    : theme.colors.lightMore,
                width: dimensions?.width - 20,
                alignSelf: "center",
                marginTop: 10,
                paddingVertical: 12,
                paddingHorizontal: 15,
                overflow: "hidden",
                ...style,
            }}
        >
            {/* Content row */}
            <View style={{ flexDirection: "row", alignItems: "center"}}>
                <FontAwesome5
                    name="calendar-check"
                    size={18}
                    color={urgencyColor}
                />
                <View style={{ marginLeft: 10, flex: 1 }}>
                    <TextM fontSize={11} color={theme.colors.grey}>
                        Prochain contrôle
                    </TextM>
                    <TextM fontSize={14} fontWeight="SemiBold" numberOfLines={1}>
                        {displayTitle} · {displayDate}
                    </TextM>
                </View>
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <View
                        style={{
                            backgroundColor: urgencyColor,
                            paddingHorizontal: 8,
                            paddingVertical: 3,
                            borderRadius: 10,
                            marginRight: 10,
                        }}
                    >
                        <TextM fontSize={11} fontWeight="Bold" color={theme.colors.white}>
                            {daysLabel}
                        </TextM>
                    </View>
                    <FontAwesome5
                        name="chevron-right"
                        size={12}
                        color={theme.colors.grey}
                    />
                </View>
            </View>

            {/* Progress bar at bottom */}
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    backgroundColor: theme.colors.lighter,
                }}
            >
                <View
                    style={{
                        width: `${Math.min(100, Math.max(0, progress))}%`,
                        height: "100%",
                        backgroundColor: urgencyColor,
                        borderBottomLeftRadius: progress < 100 ? 0 : 8,
                        borderBottomRightRadius: progress >= 100 ? 8 : 0,
                    }}
                />
            </View>
        </TouchableOpacity>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        assessmentsByStudentId: state.data.schoolAssessments?.byStudentId || {},
        loading: state.api.getSchoolAssessments?.request || false,
    }
}

const mapDispatchToProps = {
    apiRequest,
}

export default connect(mapStateToProps, mapDispatchToProps)(NextAssessmentWidget)
